import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

export default function AIFeedbackAnalysis({ data }) {
  if (!data || (!data?.feedbackPatterns && !data?.commonMistakes)) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">AI Feedback Pattern Analysis</h3>
        <div className="flex items-center justify-center h-64 text-gray-500">
          No AI feedback data available
        </div>
      </div>
    );
  }

  const { feedbackPatterns = [], commonMistakes = [] } = data;

  const COLORS = ['#4F46E5', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">AI Feedback Pattern Analysis</h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Feedback Patterns Pie Chart */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-4">AI Feedback Distribution</h4>
          {feedbackPatterns?.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={feedbackPatterns}
                  dataKey="count"
                  nameKey="category"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={(entry) => `${entry?.category}: ${entry?.count}`}
                >
                  {feedbackPatterns?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#FFF', 
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    padding: '8px 12px'
                  }}
                />
                <Legend 
                  verticalAlign="bottom"
                  height={36}
                  wrapperStyle={{ fontSize: '12px' }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-400">
              No feedback patterns available
            </div>
          )}
        </div>

        {/* Common Mistakes Bar Chart */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-4">Top 10 Common Pronunciation Challenges</h4>
          {commonMistakes?.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart 
                data={commonMistakes?.slice(0, 10)} 
                layout="vertical"
                margin={{ left: 100 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis type="number" stroke="#6B7280" style={{ fontSize: '12px' }} />
                <YAxis 
                  type="category" 
                  dataKey="mistake" 
                  stroke="#6B7280" 
                  style={{ fontSize: '11px' }}
                  width={100}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#FFF', 
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    padding: '8px 12px'
                  }}
                  formatter={(value) => [`${value} occurrences`, 'Frequency']}
                />
                <Bar 
                  dataKey="count" 
                  fill="#EF4444" 
                  radius={[0, 8, 8, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-400">
              No common mistakes data available
            </div>
          )}
        </div>
      </div>
      {/* Insights Section */}
      {commonMistakes?.length > 0 && (
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="text-sm font-semibold text-blue-900 mb-2">💡 Key Insights</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Most common challenge: <strong>{commonMistakes?.[0]?.mistake}</strong> ({commonMistakes?.[0]?.count} occurrences)</li>
            <li>• Total unique pronunciation challenges identified: <strong>{commonMistakes?.length}</strong></li>
            <li>• AI feedback provides targeted practice recommendations based on these patterns</li>
          </ul>
        </div>
      )}
    </div>
  );
}